import { Stack, Group, Text, Button, Box } from "@mantine/core";
import { ErrorState } from "@bi-studio/shared/components";
import { UseFormReturnType } from "@mantine/form";
import { ReportResultTable } from "./ReportResultTable";
import { ReportRowDetailsModal } from "./modals/ReportRowDetailsModal";
import type { ReportQueryResult } from "@bi-studio/shared/lib";
import * as React from "react";

interface ReportPreviewProps {
    form: UseFormReturnType<any>;
    onPreview: () => void;
    isPreviewing: boolean;
    previewResult: ReportQueryResult | null;
    error: string | null;
    onTroubleshoot: (error: string) => void;
    isSaving?: boolean;
}

export function ReportPreview({
    form,
    onPreview,
    isPreviewing,
    previewResult,
    error,
    onTroubleshoot,
    isSaving = false
}: ReportPreviewProps) {
    const [selectedRow, setSelectedRow] = React.useState<any | null>(null);
    const [modalOpened, setModalOpened] = React.useState(false);
    const sortedCols = React.useMemo(() => {
        const currentColumns = form.values.columns || [];
        if (currentColumns.length > 0) {
            return [...currentColumns].sort((a, b) => a.order - b.order);
        }
        return previewResult?.columns || [];
    }, [form.values.columns, previewResult?.columns]);

    return (
        <Stack gap="md">
            <Group justify="space-between">
                <Text fw={500} size="sm">Preview Results</Text>
                <Button
                    variant="light"
                    color="gray"
                    size="xs"
                    onClick={onPreview}
                    disabled={!form.values.dataSourceId || !form.values.query?.trim() || isPreviewing || isSaving}
                    loading={isPreviewing}
                >
                    Preview Query
                </Button>
            </Group>

            {error ? (
                <ErrorState
                    title="Query Error"
                    message={error}
                    actionLabel="Troubleshoot with AI"
                    onAction={() => onTroubleshoot(error)}
                />
            ) : previewResult && previewResult.rows.length > 0 ? (
                <Stack gap="xs">
                    <Text fw={500} size="sm">Query Preview ({previewResult.rowCount} rows)</Text>
                    <Box style={{ borderRadius: 'var(--mantine-radius-md)', border: '1px solid var(--modal-border)', overflow: 'hidden' }}>
                        <ReportResultTable 
                            previewResult={previewResult}
                            isLoading={isPreviewing}
                            columns={sortedCols}
                            maxRows={10}
                            onRowClick={(row) => {
                                setSelectedRow(row);
                                setModalOpened(true);
                            }}
                        />
                        {previewResult.rows.length > 10 && (
                            <Box p="xs" style={{ borderTop: '1px solid var(--modal-border)' }}>
                                <Text size="xs" c="dimmed" ta="center">
                                    Showing first 10 of {previewResult.rowCount} rows
                                </Text>
                            </Box>
                        )}
                    </Box>
                </Stack>
            ) : (
                <Box p="xl" style={{ borderRadius: 'var(--mantine-radius-md)', border: '1px solid var(--modal-border)', textAlign: 'center' }}>
                    <Text c="dimmed">Enter a query in the "Query" tab and click "Preview Query" to see results here.</Text>
                </Box>
            )}

            <ReportRowDetailsModal 
                opened={modalOpened}
                onClose={() => setModalOpened(false)}
                selectedRow={selectedRow}
            />
        </Stack>
    );
}
